import React from 'react'

import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons'
import { Box, Checkbox, ListItem, ListItemText } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { TopicFilter } from '@scoir/college-content'
import { Dropdown, SelectableIconChips } from '@scoir/college-search'

import ContentManagementContext from '../../../ContentManagementProvider/Context'

const useStyles = makeStyles((theme) => ({
  icon: {
    width: theme.spacing(2.25),
    height: theme.spacing(2.25),
  },
  nested: {
    paddingLeft: theme.spacing(6),
  },
}))

const Filter = () => {
  const classes = useStyles()
  const { content, filterByStatus, filterByType, filterByTopics, untaggedOnly, handleTableFiltering } =
    React.useContext(ContentManagementContext)

  /** Type Filter */
  const types = [
    {
      label: 'Post',
      value: 'post',
      subItems: [
        { label: 'Overview', value: 'CollegeOverview' },
        { label: 'Academics', value: 'Academics' },
        { label: 'Admissions', value: 'Admissions' },
        { label: 'Cost & Aid', value: 'CostAndFinancialAid' },
        { label: 'Student Life', value: 'StudentLife' },
      ],
    },
    { label: 'Event', value: 'Sessions' },
    { label: 'Program', value: 'Programs' },
  ]

  const getTypeChecked = (value: string) => {
    if (value === 'post') {
      const postChecked =
        filterByType?.includes('CollegeOverview') &&
        filterByType.includes('Academics') &&
        filterByType.includes('Admissions') &&
        filterByType.includes('CostAndFinancialAid') &&
        filterByType.includes('StudentLife')
      return Boolean(postChecked)
    }
    return Boolean(filterByType?.includes(value))
  }
  const getPostIndeterminate = () => {
    if (getTypeChecked('post') !== true) {
      // All post content items are not selected
      // Check if any post content items are selected
      return (
        filterByType?.includes('CollegeOverview') ||
        filterByType?.includes('Academics') ||
        filterByType?.includes('Admissions') ||
        filterByType?.includes('CostAndFinancialAid') ||
        filterByType?.includes('StudentLife')
      )
    }
    // All post content items are selected || no post content items are selected
    return false
  }

  const getSelectedTypeFilters = () => {
    const selected: string[] = []
    if (
      filterByType?.includes('CollegeOverview') &&
      filterByType.includes('Academics') &&
      filterByType.includes('Admissions') &&
      filterByType.includes('CostAndFinancialAid') &&
      filterByType.includes('StudentLife')
    ) {
      selected.push('Post')
    } else {
      if (filterByType?.includes('CollegeOverview')) {
        selected.push('Overview')
      }
      if (filterByType?.includes('Academics')) {
        selected.push('Academic')
      }
      if (filterByType?.includes('Admissions')) {
        selected.push('Admission')
      }
      if (filterByType?.includes('CostAndFinancialAid')) {
        selected.push('Cost & Aid')
      }
      if (filterByType?.includes('StudentLife')) {
        selected.push('Student Life')
      }
    }

    if (filterByType?.includes('Programs')) {
      selected.push('Program')
    }

    if (filterByType?.includes('Sessions')) {
      selected.push('Event')
    }
    return selected
  }

  const handleTypeFilter = (value: string | null) => {
    if (value === null) {
      handleTableFiltering('Type')
      return
    }
    if (value === 'post') {
      if (getPostIndeterminate() || getTypeChecked('post')) {
        // Some or All post content items are selected, remove them
        const removePostType =
          filterByType?.filter(
            (type) =>
              type !== 'CollegeOverview' &&
              type !== 'Academics' &&
              type !== 'Admissions' &&
              type !== 'CostAndFinancialAid' &&
              type !== 'StudentLife',
          ) || []
        handleTableFiltering('Type', removePostType)
      } else {
        // All post content items are unselected, select them
        handleTableFiltering('Type', [
          ...(filterByType || []),
          ...['CollegeOverview', 'Academics', 'Admissions', 'CostAndFinancialAid', 'StudentLife'],
        ])
      }
    } else if (filterByType && filterByType.indexOf(value) !== -1) {
      // Unselect type
      handleTableFiltering(
        'Type',
        filterByType.filter((type) => type !== value),
      )
    } else {
      // Select type
      handleTableFiltering('Type', [...(filterByType || []), value])
    }
  }

  /** Topics Filter */

  const getSelectedTopics = () => {
    if (untaggedOnly) {
      return ['Untagged']
    }
    const flatSelectedTopics: string[] = []
    if (filterByTopics) {
      if (filterByTopics.AcademicFocus) {
        flatSelectedTopics.push(...filterByTopics.AcademicFocus)
      }
      if (filterByTopics.Categories) {
        flatSelectedTopics.push(...filterByTopics.Categories)
      }
    }
    return flatSelectedTopics
  }

  const handleTopicsFilter = (selected: { AcademicFocus: string[]; Categories: string[] } | null) => {
    if (selected === null) {
      handleTableFiltering('Topics')
      handleTableFiltering('Untagged', false)
      return
    }
    handleTableFiltering('Topics', selected)
  }

  const handleDisplayNone = () => {
    handleTableFiltering('Untagged', !untaggedOnly)
  }

  /** Status Filter */
  const getSelectedStatusFilter = () => {
    const selected: string[] = []
    if (filterByStatus) {
      selected.push('Hidden')
    } else if (filterByStatus === false) {
      selected.push('Live')
    }
    return selected
  }
  const handleStatusFilter = (value: boolean | null) => {
    if (value === null || value === filterByStatus) {
      // Clear or unselect selected status
      handleTableFiltering('Status')
    } else {
      // Select status
      handleTableFiltering('Status', value)
    }
  }

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gridGap={{ xs: 8, sm: 0 }}
    >
      <Dropdown
        fieldLabel="Type"
        buttonLabel="Type"
        selectionLabels={getSelectedTypeFilters()}
        onClear={() => handleTypeFilter(null)}
      >
        <Box mx={2}>
          {types.map((type) => (
            <React.Fragment key={type.label}>
              <ListItem
                dense
                button
                onClick={() => handleTypeFilter(type.value)}
              >
                <Checkbox
                  edge="start"
                  color="primary"
                  checked={getTypeChecked(type.value)}
                  indeterminate={type.value === 'post' ? getPostIndeterminate() : false}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': type.value }}
                />
                <ListItemText
                  id={type.value}
                  primary={type.label}
                />
              </ListItem>
              {type.subItems?.map((item) => (
                <ListItem
                  key={item.label}
                  className={classes.nested}
                  dense
                  button
                  onClick={() => handleTypeFilter(item.value)}
                >
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={getTypeChecked(item.value)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': item.value }}
                  />
                  <ListItemText
                    id={item.value}
                    primary={item.label}
                  />
                </ListItem>
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Dropdown>

      <Dropdown
        fieldLabel="Topics"
        buttonLabel="Topics"
        selectionLabels={getSelectedTopics()}
        onClear={() => handleTopicsFilter(null)}
      >
        <TopicFilter
          topics={content?.Tags}
          onUpdateSelection={handleTopicsFilter}
          selected={filterByTopics}
          displayNone={untaggedOnly}
          handleDisplayNone={handleDisplayNone}
        />
      </Dropdown>

      <Dropdown
        fieldLabel="Status"
        buttonLabel="Status"
        selectionLabels={getSelectedStatusFilter()}
        onClear={() => handleStatusFilter(null)}
      >
        <Box m={2}>
          <SelectableIconChips
            gridItemProps={{ xs: 6 }}
            onSelect={handleStatusFilter}
            options={[
              {
                title: 'Live',
                faIcon: faEye,
                value: false,
                selected: filterByStatus === false,
              },
              {
                title: 'Hidden',
                faIcon: faEyeSlash,
                value: true,
                selected: filterByStatus,
              },
            ]}
          />
        </Box>
      </Dropdown>
    </Box>
  )
}
export default Filter
